import * as React from 'react';

import classNames from 'classnames';

import type {
    GetQuoteById_quote_productLineItems_edges_node as ProductLineItem,
    GetPossibleProducts_quotePossibleProducts_edges as QuotePossibleProductsEdge,
    GetQuoteById_quote_total as Money,
} from 'nutshell-graphql-types';

import {Button} from '../../button/button';
import {Card} from '../../card/card';
import {DraggableList} from '../../draggable-row/draggable-list';
import {ErrorState} from '../../error-state/error-state';
import type {SalesDocumentType} from '../../lead-sidebar/sales-documents/types';
import {LoadingPage} from '../../loading-page/loading-page';
import {Table} from '../../table/table';
import {TextField} from '../../textfield/textfield';
import {Body} from '../../typography/body';

import {
    COLUMN_WIDTHS,
    TEMPLATE_DATA,
    TEMPLATE_COLUMN_WIDTHS,
    COLUMN_WIDTHS_RESPONSIVE,
    FIELD_NAMES,
} from './consts';
import {
    getNameColumnWidth,
    getProductLineItemDefaults,
    mapTermEnumToLabel,
    shouldDisableDiscountToggle,
    saveUpdateProduct,
} from './helpers';
import {QuoteProductTableRowEditable} from './quote-product-table-row-editable';
import {ToggleableColumnHeader} from './toggleable-column-header';

import styles from './quote-product-table.module.css';

type Props = {
    type: SalesDocumentType;
    isLoading: boolean;
    errorMessage: string | null | undefined;
    isEditable: boolean;
    showSku: boolean;
    showTerm: boolean;
    showDiscount: boolean;
    products: Array<ProductLineItem>;
    setShowSku: (value: boolean) => void;
    setShowTerm: (value: boolean) => void;
    setShowDiscount: (value: boolean) => void;
    setProducts: (value: Array<ProductLineItem>) => void;
    quotePossibleProductsEdges: Array<QuotePossibleProductsEdge>;
    isTemplate: boolean;
    quoteTotal: Money;
    isSmallScreen: boolean;
};

export function QuoteProductTable(props: Props) {
    const {
        products,
        showSku,
        showTerm,
        showDiscount,
        setShowSku,
        setShowTerm,
        setShowDiscount,
        setProducts,
        quotePossibleProductsEdges,
        isTemplate,
        isEditable,
        quoteTotal,
        isSmallScreen,
    } = props;

    const lowerCaseType = props.type.toLowerCase();

    const className = classNames(
        'block',
        'height-fit-content',
        'width-fit-content',
        'pos-relative'
    );

    const baseWidths = isSmallScreen ? COLUMN_WIDTHS_RESPONSIVE : COLUMN_WIDTHS;
    const columnWidths = isTemplate ? TEMPLATE_COLUMN_WIDTHS : baseWidths;

    const nameColumnWidth = React.useMemo(
        () =>
            getNameColumnWidth(showSku, showTerm, showDiscount, isEditable, isTemplate, baseWidths),
        [showSku, showTerm, showDiscount, isEditable, isTemplate, baseWidths]
    );

    const newProductCounter = React.useRef(0);

    const disableDiscountToggle = shouldDisableDiscountToggle(
        isTemplate,
        products,
        setShowDiscount
    );

    const columns = React.useMemo(() => {
        return [
            {
                id: 'name',
                Header: 'Name',
                // @ts-expect-error
                accessor: (row) => row,
                Cell: ({cell}: any) => {
                    if (isTemplate) {
                        return cell.value;
                    } else {
                        return (
                            <Body color='dark' size='small' wrapAnywhere={true}>
                                {cell.value.name}
                            </Body>
                        );
                    }
                },
                dataType: 'string',
                minWidth: nameColumnWidth,
                width: nameColumnWidth,
                maxWidth: nameColumnWidth,
                canResize: false,
                textIsDkGrey: true,
                isTopAligned: true,
                shouldWrap: true,
            },
            {
                id: 'sku',
                Header: () => {
                    return (
                        <ToggleableColumnHeader
                            title='SKU'
                            showColumn={showSku}
                            setColumnState={setShowSku}
                            showToggle={isEditable || isTemplate}
                        />
                    );
                },
                // @ts-expect-error
                accessor: (row) => {
                    if (!showSku) return null;

                    return isTemplate ? row : row.sku;
                },
                dataType: 'string',
                minWidth: columnWidths.sku,
                width: columnWidths.sku,
                maxWidth: columnWidths.sku,
                canResize: false,
                textIsDkGrey: true,
                isTopAligned: true,
                shouldWrap: true,
                hideColumn: isEditable || isTemplate ? false : !showSku,
            },
            {
                id: 'quantity',
                Header: 'Quantity',
                // @ts-expect-error
                accessor: (row) => (isTemplate ? row : row.formattedQuantity),
                dataType: 'string',
                minWidth: columnWidths.quantity,
                width: columnWidths.quantity,
                maxWidth: columnWidths.quantity,
                canResize: false,
                textIsDkGrey: true,
                isTopAligned: true,
                shouldWrap: true,
                rightAlign: true,
                leftAlignHeader: true,
            },
            {
                id: 'price',
                Header: 'Price',
                // @ts-expect-error
                accessor: (row) => (isTemplate ? row : row.price.formattedWithCents),
                dataType: 'integer',
                minWidth: columnWidths.price,
                width: columnWidths.price,
                maxWidth: columnWidths.price,
                canResize: false,
                textIsDkGrey: true,
                isTopAligned: true,
                shouldWrap: true,
                rightAlign: true,
                leftAlignHeader: true,
            },
            {
                id: 'term',
                Header: () => {
                    return (
                        <ToggleableColumnHeader
                            title='Term'
                            showColumn={showTerm}
                            setColumnState={setShowTerm}
                            showToggle={isEditable || isTemplate}
                        />
                    );
                },
                // @ts-expect-error
                accessor: (row) => {
                    if (!showTerm) return null;

                    return isTemplate ? row : mapTermEnumToLabel(row.term);
                },
                dataType: 'string',
                minWidth: columnWidths.term,
                width: columnWidths.term,
                maxWidth: columnWidths.term,
                canResize: false,
                textIsDkGrey: true,
                isTopAligned: true,
                shouldTruncate: isTemplate,
                rightAlign: true,
                leftAlignHeader: true,
                hideColumn: isEditable || isTemplate ? false : !showTerm,
            },
            {
                id: 'discount',
                Header: () => {
                    return (
                        <ToggleableColumnHeader
                            title='Discount'
                            showColumn={showDiscount}
                            setColumnState={setShowDiscount}
                            showToggle={isEditable || isTemplate}
                            isDisabled={disableDiscountToggle}
                        />
                    );
                },
                // @ts-expect-error
                accessor: (row) => {
                    if (!showDiscount) return null;
                    if (isTemplate) return row;

                    return row.formattedDiscount;
                },
                dataType: 'string',
                minWidth: columnWidths.discount,
                width: columnWidths.discount,
                maxWidth: columnWidths.discount,
                canResize: false,
                textIsDkGrey: true,
                isTopAligned: true,
                shouldWrap: true,
                shouldTruncate: isTemplate,
                rightAlign: true,
                leftAlignHeader: true,
                hideColumn: isEditable || isTemplate ? false : !showDiscount,
            },
            {
                id: 'total',
                Header: 'Total',
                // @ts-expect-error
                accessor: (row) => (isTemplate ? row : row.total.formattedWithCents),
                Cell: ({cell}: any) => {
                    return (
                        <Body color='dark' isBold={true} size='small'>
                            {cell.value}
                        </Body>
                    );
                },
                dataType: 'integer',
                minWidth: columnWidths.total,
                width: columnWidths.total,
                maxWidth: columnWidths.total,
                canResize: false,
                textIsDkGrey: true,
                isTopAligned: true,
                shouldWrap: true,
                shouldTruncate: isTemplate,
                rightAlign: true,
                leftAlignHeader: true,
            },
        ];
    }, [
        isEditable,
        showSku,
        showTerm,
        showDiscount,
        setShowSku,
        setShowTerm,
        setShowDiscount,
        nameColumnWidth,
        isTemplate,
        disableDiscountToggle,
        columnWidths,
    ]);

    const handleSaveRow = React.useCallback(
        (index: number, updatedProduct: ProductLineItem) => {
            const updatedProducts = [...props.products];
            updatedProducts[index] = updatedProduct;
            setProducts(updatedProducts);
        },
        [props.products, setProducts]
    );

    const handleDeleteItem = React.useCallback(
        (index: number) => {
            const updatedProducts = [...props.products];
            updatedProducts.splice(index, 1);
            setProducts(updatedProducts);
        },
        [props.products, setProducts]
    );

    const handleAddItem = React.useCallback(() => {
        setProducts([
            // @ts-expect-error
            ...products,
            // @ts-expect-error
            getProductLineItemDefaults(
                products.length,
                newProductCounter.current++,
                products,
                quoteTotal
            ),
        ]);
    }, [products, setProducts, quoteTotal]);

    const handleReorder = React.useCallback(
        (startIndex, endIndex) => {
            const updatedProducts = [...props.products];
            const [moved] = updatedProducts.splice(startIndex, 1);
            // @ts-expect-error
            updatedProducts.splice(endIndex, 0, moved);
            setProducts(updatedProducts);
        },
        [props.products, setProducts]
    );

    const draggableItems = React.useMemo(
        () =>
            products.map((item, index) => ({
                id: item.id,
                component: (
                    <div>
                        <QuoteProductTableRowEditable
                            key={item.id}
                            item={item}
                            index={index}
                            handleSave={handleSaveRow}
                            handleDelete={handleDeleteItem}
                            showSku={showSku}
                            showTerm={showTerm}
                            showDiscount={showDiscount}
                            quotePossibleProductsEdges={quotePossibleProductsEdges}
                            isSmallScreen={isSmallScreen}
                        />
                        <div className='px-8 pb-16'>
                            <TextField
                                placeholder='Enter a description about this product'
                                rows={2}
                                multiline={true}
                                value={item.description ? item.description : ''}
                                onChange={(value) =>
                                    saveUpdateProduct(
                                        item,
                                        FIELD_NAMES.description,
                                        undefined,
                                        value,
                                        handleSaveRow,
                                        index
                                    )
                                }
                            />
                        </div>
                    </div>
                ),
            })),
        [
            showSku,
            showTerm,
            showDiscount,
            products,
            handleSaveRow,
            handleDeleteItem,
            quotePossibleProductsEdges,
            isSmallScreen,
        ]
    );

    return (
        <div className={className}>
            <Table
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                hasHoverState={false}
                hasHeaderRightBorder={false}
                hasBorders={true}
                isResizable={false}
                extendToFitWidth={false}
                isCheckboxSelectable={false}
                clickingSelectsRow={false}
                disableFilters={true}
                disableSorting={true}
                fullWidth={true}
                shouldPreventSelectAll={false}
                headerBackgroundColor='offWhiteDk'
                hasLessBottomPadding={true}
                // @ts-expect-error
                columns={columns}
                // @ts-expect-error
                data={isTemplate ? TEMPLATE_DATA : props.products}
                preserveHeaderInSpecialState={true}
                expandProps={{
                    renderExpandCell: () => <></>,
                    renderSubComponent: (row) => (
                        <div className='px-8'>
                            <Body
                                color='light'
                                size='small'
                                wrapAnywhere={true}
                                maintainLineBreaks={true}
                            >
                                {row.original.description}
                            </Body>
                        </div>
                    ),
                    expandRowIndex: undefined,
                    hideExpandCell: true,
                    matchTableWidth: true,
                    expandAllRows: true,
                }}
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.errorMessage),
                        component: <ErrorState errorMessage={props.errorMessage} size='small' />,
                    },
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage />,
                    },
                    {
                        shouldRender: () => isEditable && !isTemplate,
                        component: (
                            <div className='pt-8'>
                                <DraggableList
                                    handleReorder={handleReorder}
                                    listItems={draggableItems}
                                    showOverflow={true}
                                    isInModal={true}
                                />
                            </div>
                        ),
                    },
                ]}
            />
            {isTemplate ? (
                <div className={styles['template-text']}>
                    <Card
                        hasBorder={true}
                        hasShadow={true}
                        borderRadius={3}
                        backgroundColor='white'
                    >
                        <div className='pad-16'>
                            <Body size='normal' color='dark'>
                                Choose which columns appear by default in your {lowerCaseType}’s
                                product table by clicking the eyeballs above. Products from your
                                lead will appear here when a real {lowerCaseType} is generated.
                            </Body>
                        </div>
                    </Card>
                </div>
            ) : (
                <div className='pad-16'>
                    <Button
                        onClick={handleAddItem}
                        variant='text-primary'
                        size='xlarge'
                        noPadding={true}
                    >
                        + Add an item
                    </Button>
                </div>
            )}
        </div>
    );
}
